import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { authFetch, authJsonFetch } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Download, 
  Search, 
  ChevronRight, 
  ChevronDown,
  ChevronUp,
  Database,
  Loader2,
  FileSpreadsheet,
  Eye,
  X,
  Check,
  Calendar,
  RefreshCw,
  Upload,
  FileUp,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  History,
  RotateCcw,
  Trash2,
  Info,
  Edit,
  ExternalLink,
  Plus,
  ArrowUpDown,
  Filter,
  ArrowUp,
  ArrowDown,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { OrgQuickView } from '../components/OrgQuickView';
import { JobFlagsCell } from '../components/JobFlagsCell';
import { useManagedYears } from '../hooks/useManagedYears';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';
import BulkEmailCampaignDialog from '../components/BulkEmailCampaignDialog';
import { ImportSection } from '../components/export/ImportSection';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Date fields that should be formatted as DD-MMM-YYYY
// Date fields that should be formatted as DD-MMM-YYYY
// These will match columns with year prefixes like "2025 Photography Date 1"
const DATE_FIELDS = [
  // Photography Section
  'job_confirmed', 'job confirmed',
  
  // Sales Section
  'confirmation_letter_sent', 'confirmation letter sent',
  'date_booked', 'date booked',
  'photography_date_1', 'photography date 1',
  'photography_date_2', 'photography date 2',
  'photography_date_3', 'photography date 3',
  'photography_date_4', 'photography date 4',
  'photography_date_5', 'photography date 5',
  'sample_sent', 'sample sent',
  'term_call', 'term call',
  'appointment_date', 'appointment date',
  
  // Production Section
  'date_downloaded', 'date downloaded',
  'date_due_out', 'date due out',
  'date_envelopes_sent', 'date envelopes sent',
  'date_job_assigned', 'date job assigned',
  'date_job_packed', 'date job packed',
  'date_job_printed', 'date job printed',
  'date_names_typed', 'date names typed',
  'date_ripped', 'date ripped',
  'dispatch_date', 'dispatch date',
  'job_sheet_printed', 'job sheet printed',
  
  // Accounts Section
  'claim_form_received', 'claim form received',
  'commission_sent_date', 'commission sent date',
  
  // Additional common date fields
  'photographer_day', 'photographer day',
  'photographer_day_1', 'photographer day 1',
  'photographer_day_2', 'photographer day 2', 
  'photographer_day_3', 'photographer day 3',
  'photography_date', 'photography date',
  'created_at', 'created at',
  'updated_at', 'updated at'
];

// Format date as DD-MMM-YYYY (e.g., 15-Jan-2024)
// Handles: ISO strings, Date objects, and Excel serial numbers (like 45244)
const formatDateDDMMMYYYY = (value) => {
  if (!value) return value;
  
  try {
    let date;
    
    // Check if it's an Excel serial number (number between 1 and 100000)
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue > 0 && numValue < 100000 && Number.isInteger(numValue)) {
      // Excel serial date: days since December 30, 1899
      // Excel incorrectly considers 1900 a leap year, so subtract 1 for dates after Feb 28, 1900
      const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899
      date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
    } else {
      // Try parsing as regular date string
      date = new Date(value);
    }
    
    if (isNaN(date.getTime())) return value;
    
    // Sanity check - year should be reasonable (1900-2100)
    const year = date.getFullYear();
    if (year < 1900 || year > 2100) return value;
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    
    return `${day}-${month}-${year}`;
  } catch {
    return value;
  }
};

// Check if a field should be formatted as date
// Handles column names with year prefixes like "2025 Photography Date 1", "2024 Date Booked", etc.
const isDateField = (fieldName) => {
  if (!fieldName) return false;
  
  // Remove year prefix and normalize (e.g., "2025 Photography Date 1" -> "photography date 1")
  const lowerField = fieldName.toLowerCase();
  
  // Remove any year prefix (4 digits followed by space)
  const withoutYear = lowerField.replace(/^\d{4}\s+/, '');
  
  // Check if any date field name matches
  return DATE_FIELDS.some(df => {
    const lowerDf = df.toLowerCase();
    // Check exact match after removing year
    if (withoutYear === lowerDf) return true;
    // Check if the field contains the date pattern
    if (withoutYear.includes(lowerDf)) return true;
    if (lowerField.includes(lowerDf)) return true;
    // Check normalized versions (remove underscores/spaces)
    const normalizedField = withoutYear.replace(/[_\s]+/g, '');
    const normalizedDf = lowerDf.replace(/[_\s]+/g, '');
    if (normalizedField === normalizedDf) return true;
    if (normalizedField.includes(normalizedDf)) return true;
    return false;
  });
};

// Format cell value - applies date formatting if needed
const formatCellValue = (value, columnName) => {
  if (value === null || value === undefined || value === '') return '-';
  
  // Check if this is a date field by column name
  if (isDateField(columnName)) {
    return formatDateDDMMMYYYY(value);
  }
  
  // Also auto-detect Excel serial dates (number between 35000-60000 covers ~1995-2064)
  const numValue = Number(value);
  if (!isNaN(numValue) && numValue >= 35000 && numValue <= 60000 && Number.isInteger(numValue)) {
    // This is likely an Excel serial date, convert it
    return formatDateDDMMMYYYY(value);
  }
  
  return String(value);
};

// Generate year options (current year back to 2018) — fallback list used
// when the managed-years API hasn't responded yet. The in-component
// `useManagedYears()` hook replaces this with the live union of data +
// admin-added years.
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYear - 2017 }, (_, i) => currentYear - i);

// Tables that have year-based data (per org per year)
const YEAR_BASED_TABLES = ['sales', 'sales_data', 'refunds', 'workflows', 'orders'];

// Helper to format field labels
const formatFieldLabel = (field) => {
  return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Get badge color based on type
const getTypeBadgeColor = (type) => {
  switch (type) {
    case 'string': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'number': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'boolean': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'date': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    case 'reference': return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
};

// ==================== IMPORT COMPONENT (moved) ====================
// `ImportSection` was extracted to `components/export/ImportSection.jsx`
// on Oct 2 2026 to slim down this page. See the import at the top of this file.


// ==================== MAIN EXPORT PAGE ====================
export const ExportDataPage = () => {
  const { isBarbiePink } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState({});
  const [activeTab, setActiveTab] = useState('export');
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.email === 'j.win@gsimaging.com.au';
  
  // Multi-year selection
  const [selectedYears, setSelectedYears] = useState([currentYear]);

  // Managed-years list (data years ∪ admin-added years). Replaces the
  // module-level `yearOptions` fallback once it loads.
  const managedYears = useManagedYears();
  const dynamicYearOptions = React.useMemo(() => {
    const merged = new Set([
      ...(yearOptions || []),
      ...(managedYears.years || []),
    ]);
    return Array.from(merged).sort((a, b) => b - a);
  }, [managedYears.years]);

  // Unsaved-changes guard — prompt before switching org from the top
  // table while the bottom panel still has dirty sales edits.
  const [quickViewDirty, setQuickViewDirty] = useState(false);
  const quickViewSaveRef = useRef(null);
  const [switchOrgDialog, setSwitchOrgDialog] = useState({ open: false, targetOrgId: null });

  // Quick-view (split-pane in Data Preview Dialog) state is declared up
  // here (ahead of `requestSwitchOrg`) because the callback below reads
  // `quickViewOrgId` in its deps array — reordering avoids a TDZ
  // ReferenceError on first render.
  const [quickViewOrgId, setQuickViewOrgId] = useState(null);
  const [quickViewYear, setQuickViewYear] = useState(new Date().getFullYear());
  const [quickViewTab, setQuickViewTab] = useState('sales');
  const [quickViewExpanded, setQuickViewExpanded] = useState(false); // Default collapsed

  const requestSwitchOrg = useCallback((targetOrgId) => {
    if (!targetOrgId) return;
    if (targetOrgId === quickViewOrgId) return;
    if (quickViewDirty) {
      setSwitchOrgDialog({ open: true, targetOrgId });
      return;
    }
    setQuickViewOrgId(targetOrgId);
    setQuickViewExpanded(true);
  }, [quickViewOrgId, quickViewDirty]);
  
  // Field selections per table
  const [selections, setSelections] = useState({});
  
  // Per-year column exclusions: Set<"{year}_{table}_{field}">
  // Lets users drop a single year's column for a field while keeping the same
  // field for other selected years (e.g. drop "2026 Photographers" but keep "2025 Photographers").
  const [excludedYearFields, setExcludedYearFields] = useState(new Set());
  
  const [expandedTables, setExpandedTables] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  // Job flags overlay for the Data Preview table. Fetched once when the
  // preview loads and kept in sync via `handleFlagsChangeForOrg` when the
  // user toggles chips inside a cell. Shape:
  //   { [orgId]: [{ flag_type, note, year, flag_id, ... }, ...] }
  const [previewFlagsByOrg, setPreviewFlagsByOrg] = useState({});
  
  // Preview sorting and filtering state
  const [previewSortColumn, setPreviewSortColumn] = useState(null);
  const [previewSortDirection, setPreviewSortDirection] = useState('asc');
  const [previewFilters, setPreviewFilters] = useState({}); // { columnName: { text: '', showBlanks: false, hideBlanks: false, months: [] } }
  const [openFilterPopover, setOpenFilterPopover] = useState(null);

  // Quick-view (split-pane in Data Preview Dialog)
  // Sticky tab + year persist as user clicks between orgs.
  // (State declarations moved up above `requestSwitchOrg` — see earlier
  // in this component.)
  // Per-column widths in the Data Preview table (px). Empty = auto.
  const [previewColumnWidths, setPreviewColumnWidths] = useState({});
  // Per-preview column order — array of column names. When null, the table
  // renders columns in their original `previewData.columns` order. Drag &
  // drop on the header reorders this array; it's saved alongside the rest
  // of the view-state on a saved search.
  const [previewColumnOrder, setPreviewColumnOrder] = useState(null);
  // Track which column the user is currently dragging.
  const dragColumnRef = useRef(null);
  const resizingColumnRef = useRef(null);
  // When the user loads a saved search that carries view state, we stash the
  // filters/sort/widths here. The next time `previewData` arrives, an effect
  // applies them in one shot so the table shows exactly the same view that
  // was saved. Cleared after application.
  const pendingViewStateRef = useRef(null);
  
  // Month names for date filtering
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Saved searches state
  const [savedSearches, setSavedSearches] = useState([]);
  const [loadingSavedSearches, setLoadingSavedSearches] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', description: '', is_shared: false, save_view_state: false });
  const [deletingSearch, setDeletingSearch] = useState(null);
  const [editingSearch, setEditingSearch] = useState(null);  // For editing existing search
  const [savedSearchesOpen, setSavedSearchesOpen] = useState(true);  // Collapsible state
  
  // Navigation
  const navigate = useNavigate();

  // Column-resize handlers (mouse drag on header edge updates `previewColumnWidths`)
  const startColumnResize = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.currentTarget.closest('th');
    const startWidth = th ? th.getBoundingClientRect().width : 120;
    resizingColumnRef.current = { col, startX, startWidth };

    const onMove = (ev) => {
      const ref = resizingColumnRef.current;
      if (!ref) return;
      const delta = ev.clientX - ref.startX;
      const newWidth = Math.max(60, ref.startWidth + delta);
      setPreviewColumnWidths(prev => ({ ...prev, [ref.col]: newWidth }));
    };
    const onUp = () => {
      resizingColumnRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  // Fetch schema from backend (backend already filters out system tables)
  const fetchSchema = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/export-data/schema`);
      if (response.ok) {
        const data = await response.json();
        const processedSchema = {};
        
        // Process schema - add yearBased flag
        Object.entries(data.schema || {}).forEach(([tableName, tableData]) => {
          processedSchema[tableName] = {
            ...tableData,
            yearBased: YEAR_BASED_TABLES.includes(tableName)
          };
        });
        
        setSchema(processedSchema);
        
        // Initialize selections for all tables - include 'name' by default for organizations
        const initialSelections = {};
        Object.keys(processedSchema).forEach(table => {
          if (table === 'organizations' && processedSchema[table]?.fields?.name) {
            initialSelections[table] = ['name'];
          } else {
            initialSelections[table] = [];
          }
        });
        setSelections(initialSelections);
        
        // Expand organizations by default
        setExpandedTables({ organizations: true });
      } else {
        toast.error('Failed to load database schema');
      }
    } catch (err) {
      console.error('Schema fetch error:', err);
      toast.error('Failed to load database schema');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  // Fetch saved searches
  const fetchSavedSearches = useCallback(async () => {
    setLoadingSavedSearches(true);
    try {
      const response = await authFetch(`${API}/export-data/saved-searches`);
      if (response.ok) {
        const data = await response.json();
        setSavedSearches(data.saved_searches || []);
      }
    } catch (err) {
      console.error('Failed to fetch saved searches:', err);
    } finally {
      setLoadingSavedSearches(false);
    }
  }, []);

  useEffect(() => {
    fetchSavedSearches();
  }, [fetchSavedSearches]);

  // Auto-load the saved search referenced by ?load=<search_id> in the URL.
  // The Dashboard's "frequently used" pill bar jumps here with that param,
  // letting operators go straight from "I want my Term 4 view" to looking
  // at it in one click. We wait for the savedSearches list to populate so
  // we can resolve the id locally — saves a redundant fetch.
  const [autoLoadAttempted, setAutoLoadAttempted] = useState(false);
  useEffect(() => {
    if (autoLoadAttempted) return;
    if (!savedSearches || savedSearches.length === 0) return;
    let loadId = null;
    try {
      const params = new URLSearchParams(window.location.search);
      loadId = params.get('load');
    } catch {
      return;
    }
    if (!loadId) {
      setAutoLoadAttempted(true);
      return;
    }
    const match = savedSearches.find((s) => s.search_id === loadId);
    if (match) {
      handleLoadSearch(match);
    }
    setAutoLoadAttempted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearches]);

  // Save current search configuration
  const handleSaveSearch = async () => {
    if (!saveForm.name.trim()) {
      toast.error('Please enter a name for the search');
      return;
    }
    
    if (totalSelectedFields === 0) {
      toast.error('Please select at least one field first');
      return;
    }
    
    setSavingSearch(true);
    try {
      // Determine if we're creating or updating
      const isUpdate = !!editingSearch;
      const url = isUpdate 
        ? `${API}/export-data/saved-searches/${editingSearch.search_id}`
        : `${API}/export-data/saved-searches`;
      
      const response = await authJsonFetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        body: JSON.stringify({
          name: saveForm.name.trim(),
          description: saveForm.description.trim(),
          selections,
          years: selectedYears,
          is_shared: saveForm.is_shared,
          // Only persist preview state when the user explicitly opted in via
          // the "Save current view state" toggle AND we actually have a
          // preview open with usable state.
          preview_filters: saveForm.save_view_state ? previewFilters : null,
          preview_sort_column: saveForm.save_view_state ? previewSortColumn : null,
          preview_sort_direction: saveForm.save_view_state ? previewSortDirection : null,
          preview_column_widths: saveForm.save_view_state ? previewColumnWidths : null,
          preview_column_order: saveForm.save_view_state ? previewColumnOrder : null,
          // Per-year field un-ticks are part of the SELECTION semantics — not
          // just the view chrome — so we ALWAYS persist them, regardless of
          // the "Save current view state" toggle. Otherwise loading a search
          // re-introduces fields the user explicitly removed for a given
          // year. (See ExportDataPage handleLoadSearch.)
          excluded_year_fields: Array.from(excludedYearFields),
          has_view_state: !!saveForm.save_view_state
        })
      });
      
      if (response.ok) {
        toast.success(isUpdate ? 'Search updated successfully' : 'Search saved successfully');
        setSaveDialogOpen(false);
        setSaveForm({ name: '', description: '', is_shared: false, save_view_state: false });
        setEditingSearch(null);
        // If we just created a brand-new search via "Save as new", clear the
        // loaded-view tracker so subsequent Save View clicks default to "new"
        // again rather than offering to update the previously-loaded view.
        if (!isUpdate) {
          setLoadedView(null);
        }
        fetchSavedSearches();
      } else {
        const err = await response.json();
        toast.error(err.detail || `Failed to ${isUpdate ? 'update' : 'save'} search`);
      }
    } catch (err) {
      toast.error(`Failed to ${editingSearch ? 'update' : 'save'} search`);
    } finally {
      setSavingSearch(false);
    }
  };

  // Start editing a saved search
  // True iff the current user owns this saved search (or is admin). The
  // backend ALREADY enforces this server-side; this client check is purely
  // to hide UI affordances that would otherwise look interactive but 403.
  // Non-creators see the Save-as-New flow instead of the Edit / Update
  // controls.
  const canEditSearch = useCallback((search) => {
    if (!search || !user) return false;
    if (search.created_by === user.user_id) return true;
    if (user.role === 'admin') return true;
    return false;
  }, [user]);

  const handleEditSearch = (search) => {
    // Load the search configuration
    setSelections(search.selections || {});
    setSelectedYears(search.years || [currentYear]);
    setPreviewData(null);
    // Set up the edit form
    setSaveForm({
      name: search.name,
      description: search.description || '',
      is_shared: search.is_shared || false,
      // Pre-tick the view-state toggle if this search was originally saved
      // with view state, so an "Update" round-trip preserves it unless the
      // user explicitly opts out.
      save_view_state: !!search.has_view_state
    });
    setEditingSearch(search);
    setSaveDialogOpen(true);
    toast.info(`Editing: ${search.name}`);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingSearch(null);
    setSaveForm({ name: '', description: '', is_shared: false, save_view_state: false });
    setSaveDialogOpen(false);
  };

  // Tracks the currently-loaded saved view (only set for view-state searches)
  // so the "Update view" shortcut in the preview footer knows what to PATCH.
  // Cleared when the user explicitly starts fresh or saves as new.
  const [loadedView, setLoadedView] = useState(null);
  const [updatingView, setUpdatingView] = useState(false);

  // Inline Bulk Email Campaign dialog state — replaces the legacy
  // navigate('/email-hub', {prefillBulk:…}) path which suffered from a
  // race condition that produced phantom recipients. The dialog reads
  // the exact org_ids passed in and never touches any other org list.
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDialogOrgIds, setBulkDialogOrgIds] = useState([]);

  // Load a saved search configuration AND immediately open the preview with
  // it. The user expectation is: click an entry in Saved Searches → that
  // exact view appears. Field-only entries still load with no filters
  // applied; view-state entries restore filters/sort/widths via the
  // pendingViewStateRef path inside handlePreview.
  const handleLoadSearch = (search) => {
    const nextSelections = search.selections || {};
    const nextYears = (search.years && search.years.length) ? search.years : [currentYear];
    setSelections(nextSelections);
    setSelectedYears(nextYears);
    // Restore per-year un-ticks too. Without this, fields the user removed
    // for a specific year (e.g. "2025 Sales Estimate") reappear on every
    // load — which is exactly the bug operators reported on Feb 25 2026.
    const nextExcluded = Array.isArray(search.excluded_year_fields)
      ? new Set(search.excluded_year_fields)
      : new Set();
    setExcludedYearFields(nextExcluded);
    setPreviewData(null);

    // Fire-and-forget usage bump so the Dashboard's "frequently used" pill
    // bar can rank this entry higher next visit. Non-blocking; errors are
    // swallowed because they don't affect the load.
    try {
      authJsonFetch(`${API}/export-data/saved-searches/${search.search_id}/use`, {
        method: 'POST',
      }).catch(() => {});
    } catch {}
    if (search.has_view_state) {
      pendingViewStateRef.current = {
        filters: search.preview_filters || {},
        sortColumn: search.preview_sort_column || null,
        sortDirection: search.preview_sort_direction || 'asc',
        columnWidths: search.preview_column_widths || {},
        columnOrder: search.preview_column_order || null,
      };
      setLoadedView({
        search_id: search.search_id,
        name: search.name,
        is_shared: !!search.is_shared,
        description: search.description || '',
        created_by: search.created_by,
      });
    } else {
      pendingViewStateRef.current = null;
      setLoadedView(null);
    }
    // Generate preview directly with the just-loaded payload — bypasses
    // setState's tick-delay so the operator doesn't have to click Preview.
    handlePreview({
      selections: nextSelections,
      years: nextYears,
      excludedYearFields: Array.from(nextExcluded),
    });
  };

  // In-place update of the currently-loaded saved view. Triggered by the
  // "Update '{name}'" button inside the preview dialog footer — saves the
  // operator a round-trip through the Edit dialog.
  const handleUpdateLoadedView = async () => {
    if (!loadedView) return;
    setUpdatingView(true);
    try {
      const response = await authJsonFetch(
        `${API}/export-data/saved-searches/${loadedView.search_id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            name: loadedView.name,
            description: loadedView.description || '',
            selections,
            years: selectedYears,
            is_shared: !!loadedView.is_shared,
            preview_filters: previewFilters,
            preview_sort_column: previewSortColumn,
            preview_sort_direction: previewSortDirection,
            preview_column_widths: previewColumnWidths,
            preview_column_order: previewColumnOrder,
            excluded_year_fields: Array.from(excludedYearFields),
            has_view_state: true,
          }),
        }
      );
      if (response.ok) {
        toast.success(`Updated "${loadedView.name}"`);
        fetchSavedSearches();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update view');
      }
    } catch (err) {
      toast.error('Failed to update view');
    } finally {
      setUpdatingView(false);
    }
  };

  // Delete a saved search
  const handleDeleteSearch = async (searchId, searchName) => {
    if (!window.confirm(`Delete saved search "${searchName}"?`)) return;
    
    setDeletingSearch(searchId);
    try {
      const response = await authFetch(`${API}/export-data/saved-searches/${searchId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Search deleted');
        fetchSavedSearches();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete search');
      }
    } catch (err) {
      toast.error('Failed to delete search');
    } finally {
      setDeletingSearch(null);
    }
  };

  // Get total selected fields count
  const totalSelectedFields = useMemo(() => {
    return Object.values(selections).reduce((total, fields) => total + fields.length, 0);
  }, [selections]);

  // Column order honouring user drag-drop. Falls back to original column
  // order. New columns appearing after a reorder (e.g. when a new field is
  // selected) are appended in their original positions.
  const FLAGS_COL = 'Flags';

  const orderedColumns = useMemo(() => {
    const original = previewData?.columns || [];
    // Inject the virtual "Flags" column immediately after the Name column so
    // it sits right next to the org label. If Name isn't present we prepend.
    const withFlags = (() => {
      if (original.includes(FLAGS_COL)) return original;
      const nameIdx = original.findIndex(c => {
        if (!c || typeof c !== 'string') return false;
        const cl = c.toLowerCase().trim();
        return cl === 'name' || cl === 'organisation name' || cl === 'organization name';
      });
      if (nameIdx === -1) return [FLAGS_COL, ...original];
      return [...original.slice(0, nameIdx + 1), FLAGS_COL, ...original.slice(nameIdx + 1)];
    })();
    if (!previewColumnOrder || previewColumnOrder.length === 0) return withFlags;
    const orderedSet = new Set(previewColumnOrder);
    const kept = previewColumnOrder.filter(c => withFlags.includes(c));
    const newCols = withFlags.filter(c => !orderedSet.has(c));
    return [...kept, ...newCols];
  }, [previewData?.columns, previewColumnOrder]);

  // Numeric helpers + auto-sum for any column whose name contains the words
  // "estimate" or "income" (case-insensitive). Totals respect active filters
  // and sort, so the figure always reflects the rows the operator can see.
  const isSummableColumn = useCallback((col) => {
    if (!col || typeof col !== 'string') return false;
    return /estimate|income/i.test(col);
  }, []);

  // The "Name" column is the operator's primary anchor for scanning rows,
  // so we freeze it (and only it) to the left edge when the table scrolls
  // horizontally. Match the same flavours we already special-case
  // elsewhere in this file ("name", "organisation name", "organization name").
  const isNameColumn = useCallback((col) => {
    if (!col || typeof col !== 'string') return false;
    const c = col.toLowerCase().trim();
    return c === 'name' || c === 'organisation name' || c === 'organization name';
  }, []);

  const parseNumericCell = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    // Strip currency, thousand separators, whitespace
    const cleaned = String(v).replace(/[$,£€¥\s]/g, '').replace(/[,]/g, '');
    if (cleaned === '' || cleaned === '-') return null;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  // Filtered and sorted preview data
  const filteredAndSortedPreview = useMemo(() => {
    if (!previewData?.preview) return [];

    // Stamp each row with a text-searchable Flags signature drawn from
    // `previewFlagsByOrg`. Format: "photography-2026 marketing-2025" —
    // a single space-separated string that the existing text filter can
    // match against and the existing string-sort can order by. The cell
    // renderer never uses this string — it pulls actual chip data from
    // the flags map — but the filter/sort pipeline needs SOMETHING in
    // `row[FLAGS_COL]` to work with.
    let data = previewData.preview.map(row => {
      const oid = row['_org_id'] || row['org_id'];
      const fl = oid ? (previewFlagsByOrg[oid] || []) : [];
      const sig = fl
        .map(f => `${f.flag_type}-${f.year}${f.note ? ' ' + f.note : ''}`)
        .join(' ');
      return { ...row, [FLAGS_COL]: sig };
    });
    
    // Apply filters
    Object.entries(previewFilters).forEach(([column, filter]) => {
      if (!filter) return;
      
      // Handle new filter format { text, showBlanks, hideBlanks, months }
      const filterObj = typeof filter === 'string' 
        ? { text: filter, showBlanks: false, hideBlanks: false, months: [] } 
        : filter;
      
      const { text, showBlanks, hideBlanks, months } = filterObj;
      
      data = data.filter(row => {
        const cellValue = row[column];
        const isBlank = cellValue === null || cellValue === undefined || cellValue === '';
        
        // Hide blanks - exclude all blank values
        if (hideBlanks && isBlank) return false;
        
        // If showBlanks is true and value is blank, include it
        if (showBlanks && isBlank) return true;
        
        // If showBlanks is the only filter and value is not blank, exclude
        if (showBlanks && !text && (!months || months.length === 0) && !isBlank) return false;
        
        // Text filter
        if (text && text.trim()) {
          const lowerFilter = text.toLowerCase();
          if (isBlank) return false;
          if (!String(cellValue).toLowerCase().includes(lowerFilter)) return false;
        }
        
        // Month filter for date fields
        if (months && months.length > 0 && isDateField(column)) {
          if (isBlank) return false;
          
          // Get the month from the value (handle Excel serial numbers and various date formats)
          let dateMonth = -1;
          const numValue = Number(cellValue);
          if (!isNaN(numValue) && numValue >= 35000 && numValue <= 60000 && Number.isInteger(numValue)) {
            // Excel serial date
            const excelEpoch = new Date(1899, 11, 30);
            const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
            dateMonth = date.getMonth();
          } else {
            // Try DD-MMM-YYYY format first (e.g., "15-Jan-2024")
            const ddMmmYyyy = String(cellValue).match(/^\d{2}-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/i);
            if (ddMmmYyyy) {
              const monthNames = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
              dateMonth = monthNames.indexOf(ddMmmYyyy[1].toLowerCase());
            } else {
              const date = new Date(cellValue);
              if (!isNaN(date.getTime())) {
                dateMonth = date.getMonth();
              }
            }
          }
          
          if (dateMonth === -1 || !months.includes(dateMonth)) return false;
        }
        
        // If value is blank and showBlanks is not active, still include it if no text/month filters matched
        if (isBlank && !showBlanks) return true;
        
        return true;
      });
    });
    
    // Apply sorting
    if (previewSortColumn) {
      // Local universal-date parser: mirrors backend `parse_any_date` so
      // sort order matches what the user sees and what backend filters use.
      // Returns a Number (timestamp) or null when not a date.
      const _MMMS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
      const _MMMSL = ['january','february','march','april','june','july','august','september','october','november','december','may'];
      const parseDateLike = (v) => {
        if (v === null || v === undefined || v === '') return null;
        if (typeof v === 'number') {
          // Excel serial range — see backend date_utils.py
          if (v >= 18000 && v <= 73100) {
            const d = new Date(Date.UTC(1899, 11, 30) + v * 86400000);
            return d.getTime();
          }
          return null;
        }
        if (typeof v !== 'string') return null;
        const s = v.trim();
        if (!s) return null;
        // Pure-numeric string → maybe Excel serial
        if (/^-?\d+(\.\d+)?$/.test(s)) {
          const n = parseFloat(s);
          if (n >= 18000 && n <= 73100) {
            return new Date(Date.UTC(1899, 11, 30) + n * 86400000).getTime();
          }
          return null;
        }
        // DD-MMM-YYYY (with optional trailing time/chars) — canonical
        const mDmmm = s.match(/^(\d{1,2})[-\s./](Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|June|July|August|September|October|November|December)[-\s./](\d{4}|\d{2})/i);
        if (mDmmm) {
          const day = parseInt(mDmmm[1], 10);
          const monStr = mDmmm[2].toLowerCase();
          let mon = _MMMS.indexOf(monStr.slice(0, 3));
          if (mon < 0) mon = _MMMSL.indexOf(monStr);
          if (mon < 0) return null;
          const yr = mDmmm[3].length === 2
            ? (parseInt(mDmmm[3], 10) <= 69 ? 2000 + parseInt(mDmmm[3], 10) : 1900 + parseInt(mDmmm[3], 10))
            : parseInt(mDmmm[3], 10);
          return new Date(Date.UTC(yr, mon, day)).getTime();
        }
        // ISO YYYY-MM-DD
        const mIso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
        if (mIso) {
          return new Date(Date.UTC(parseInt(mIso[1], 10), parseInt(mIso[2], 10) - 1, parseInt(mIso[3], 10))).getTime();
        }
        // DD/MM/YYYY (AU). Try DD/MM first; fall back to MM/DD.
        const mSlash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})/);
        if (mSlash) {
          const a = parseInt(mSlash[1], 10);
          const b = parseInt(mSlash[2], 10);
          const yRaw = mSlash[3];
          const yr = yRaw.length === 2
            ? (parseInt(yRaw, 10) <= 69 ? 2000 + parseInt(yRaw, 10) : 1900 + parseInt(yRaw, 10))
            : parseInt(yRaw, 10);
          // DD/MM if first <=12 doesn't disqualify; only switch to MM/DD if DD/MM is invalid (a > 31 or month > 12)
          if (a >= 1 && a <= 31 && b >= 1 && b <= 12) {
            const t = Date.UTC(yr, b - 1, a);
            if (!isNaN(t)) return t;
          }
          if (a >= 1 && a <= 12 && b >= 1 && b <= 31) {
            const t = Date.UTC(yr, a - 1, b);
            if (!isNaN(t)) return t;
          }
          return null;
        }
        return null;
      };

      data.sort((a, b) => {
        let aVal = a[previewSortColumn];
        let bVal = b[previewSortColumn];

        // Handle null/undefined
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        // Date comparison FIRST — string-shaped dates like "15-Jun-2026"
        // would otherwise be misread as the number 15 by parseFloat.
        const aDate = parseDateLike(aVal);
        const bDate = parseDateLike(bVal);
        if (aDate !== null && bDate !== null) {
          return previewSortDirection === 'asc' ? aDate - bDate : bDate - aDate;
        }
        // If exactly one side is a date, the date wins (sorts earlier in
        // ascending). This keeps mixed date/non-date columns predictable.
        if (aDate !== null) return previewSortDirection === 'asc' ? -1 : 1;
        if (bDate !== null) return previewSortDirection === 'asc' ? 1 : -1;

        // Try numeric comparison
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        if (!isNaN(aNum) && !isNaN(bNum) && /^-?\d+(\.\d+)?$/.test(String(aVal).trim()) && /^-?\d+(\.\d+)?$/.test(String(bVal).trim())) {
          return previewSortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // String comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (previewSortDirection === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }
    
    return data;
  }, [previewData?.preview, previewFilters, previewSortColumn, previewSortDirection, previewFlagsByOrg]);

  // Pre-compute totals for any summable (Estimate / Income) columns, using
  // the currently visible (filtered + sorted) row set. Memoised so we don't
  // re-scan every cell on unrelated re-renders.
  const columnTotals = useMemo(() => {
    const totals = {};
    const cols = previewData?.columns || [];
    cols.forEach(col => {
      if (!isSummableColumn(col)) return;
      let sum = 0;
      let count = 0;
      filteredAndSortedPreview.forEach(row => {
        const n = parseNumericCell(row[col]);
        if (n !== null) {
          sum += n;
          count += 1;
        }
      });
      if (count > 0) totals[col] = sum;
    });
    return totals;
  }, [filteredAndSortedPreview, previewData?.columns, isSummableColumn]);

  // Format a numeric total. Currency-flavoured columns (Estimate / Income)
  // tend to be money, so we use AUD with 2dp + thousands separators.
  const formatTotal = useCallback((value) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(value);
    } catch {
      return value.toFixed(2);
    }
  }, []);

  // ---------- Column drag-and-drop (header reordering) ----------
  const handleColumnDragStart = useCallback((e, col) => {
    dragColumnRef.current = col;
    try {
      e.dataTransfer.setData('text/plain', col);
      e.dataTransfer.effectAllowed = 'move';
    } catch {}
  }, []);

  const handleColumnDragOver = useCallback((e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch {}
  }, []);

  const handleColumnDrop = useCallback((e, targetCol) => {
    e.preventDefault();
    const sourceCol = dragColumnRef.current || (() => {
      try { return e.dataTransfer.getData('text/plain'); } catch { return null; }
    })();
    dragColumnRef.current = null;
    if (!sourceCol || !targetCol || sourceCol === targetCol) return;
    const current = orderedColumns.slice();
    const fromIdx = current.indexOf(sourceCol);
    const toIdx = current.indexOf(targetCol);
    if (fromIdx === -1 || toIdx === -1) return;
    current.splice(fromIdx, 1);
    current.splice(toIdx, 0, sourceCol);
    setPreviewColumnOrder(current);
  }, [orderedColumns]);

  // Handle column sort click
  const handleSortClick = (column) => {
    if (previewSortColumn === column) {
      // Toggle direction or clear
      if (previewSortDirection === 'asc') {
        setPreviewSortDirection('desc');
      } else {
        setPreviewSortColumn(null);
        setPreviewSortDirection('asc');
      }
    } else {
      setPreviewSortColumn(column);
      setPreviewSortDirection('asc');
    }
  };

  // Check if a filter object has any active criteria
  const isFilterActive = useCallback((filter) => {
    if (!filter) return false;
    if (typeof filter === 'string') return filter.trim().length > 0;
    return (filter.text && filter.text.trim().length > 0) || filter.showBlanks || filter.hideBlanks || (filter.months && filter.months.length > 0);
  }, []);

  // Check if any filter is active across all columns
  const hasAnyActiveFilter = useMemo(() => {
    return Object.values(previewFilters).some(f => isFilterActive(f));
  }, [previewFilters, isFilterActive]);

  // Handle filter change - updates a specific property of a column's filter
  const handleFilterChange = (column, property, value) => {
    setPreviewFilters(prev => {
      const existing = prev[column] || { text: '', showBlanks: false, hideBlanks: false, months: [] };
      const current = typeof existing === 'string' ? { text: existing, showBlanks: false, hideBlanks: false, months: [] } : existing;
      // Mutually exclusive: showBlanks and hideBlanks
      let updated = { ...current, [property]: value };
      if (property === 'showBlanks' && value) updated.hideBlanks = false;
      if (property === 'hideBlanks' && value) updated.showBlanks = false;
      // If all empty, remove the filter entry
      if (!updated.text?.trim() && !updated.showBlanks && !updated.hideBlanks && (!updated.months || updated.months.length === 0)) {
        const next = { ...prev };
        delete next[column];
        return next;
      }
      return { ...prev, [column]: updated };
    });
  };

  // Toggle a month in a column's month filter
  const toggleMonthFilter = (column, monthIndex) => {
    setPreviewFilters(prev => {
      const existing = prev[column] || { text: '', showBlanks: false, hideBlanks: false, months: [] };
      const current = typeof existing === 'string' ? { text: existing, showBlanks: false, hideBlanks: false, months: [] } : existing;
      const months = current.months || [];
      const updated = months.includes(monthIndex) ? months.filter(m => m !== monthIndex) : [...months, monthIndex];
      const newFilter = { ...current, months: updated };
      if (!newFilter.text?.trim() && !newFilter.showBlanks && !newFilter.hideBlanks && newFilter.months.length === 0) {
        const next = { ...prev };
        delete next[column];
        return next;
      }
      return { ...prev, [column]: newFilter };
    });
  };

  // Clear filter for a single column
  const clearColumnFilter = (column) => {
    setPreviewFilters(prev => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
    setOpenFilterPopover(null);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setPreviewFilters({});
    setPreviewSortColumn(null);
    setPreviewSortDirection('asc');
    setOpenFilterPopover(null);
  };

  // Get the current filter object for a column
  const getColumnFilter = (column) => {
    const f = previewFilters[column];
    if (!f) return { text: '', showBlanks: false, hideBlanks: false, months: [] };
    if (typeof f === 'string') return { text: f, showBlanks: false, hideBlanks: false, months: [] };
    return { text: f.text || '', showBlanks: !!f.showBlanks, hideBlanks: !!f.hideBlanks, months: f.months || [] };
  };

  // Build column headers based on selections and years
  const columnHeaders = useMemo(() => {
    const headers = [];
    
    // Non-year-based tables first
    Object.entries(selections).forEach(([tableName, fields]) => {
      if (!YEAR_BASED_TABLES.includes(tableName) && fields.length > 0) {
        fields.forEach(field => {
          const fieldData = schema[tableName]?.fields?.[field];
          headers.push({
            key: `${tableName}_${field}`,
            label: fieldData?.label || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            table: tableName,
            field
          });
        });
      }
    });
    
    // Year-based tables with year prefix
    selectedYears.sort((a, b) => b - a).forEach(year => {
      YEAR_BASED_TABLES.forEach(tableName => {
        const fields = selections[tableName] || [];
        fields.forEach(field => {
          // Skip combos that the user has individually excluded
          if (excludedYearFields.has(`${year}_${tableName}_${field}`)) return;
          const fieldData = schema[tableName]?.fields?.[field];
          headers.push({
            key: `${year}_${tableName}_${field}`,
            label: `${year} ${fieldData?.label || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
            table: tableName,
            field,
            year
          });
        });
      });
    });
    
    return headers;
  }, [selections, selectedYears, schema, excludedYearFields]);

  const toggleTable = (tableName) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  const toggleField = (table, fieldName) => {
    setSelections(prev => ({
      ...prev,
      [table]: prev[table]?.includes(fieldName)
        ? prev[table].filter(f => f !== fieldName)
        : [...(prev[table] || []), fieldName]
    }));
    // When a field is toggled off entirely, drop any per-year exclusions for it
    // so re-toggling on doesn't carry over stale exclusions.
    setExcludedYearFields(prev => {
      const next = new Set(prev);
      let changed = false;
      next.forEach(combo => {
        if (combo.endsWith(`_${table}_${fieldName}`)) {
          next.delete(combo);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setPreviewData(null);
  };

  const toggleYear = (year) => {
    setSelectedYears(prev => 
      prev.includes(year)
        ? prev.filter(y => y !== year)
        : [...prev, year].sort((a, b) => b - a)
    );
    // When a year is re-added or removed, clear any per-year exclusions tied to it
    // so re-adding the year brings back all its columns by default.
    setExcludedYearFields(prev => {
      const next = new Set(prev);
      let changed = false;
      next.forEach(combo => {
        if (combo.startsWith(`${year}_`)) {
          next.delete(combo);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setPreviewData(null);
  };

  const selectAllInTable = (table) => {
    const allFields = Object.keys(schema[table]?.fields || {});
    setSelections(prev => ({ ...prev, [table]: allFields }));
    setPreviewData(null);
  };

  const clearTable = (table) => {
    setSelections(prev => ({ ...prev, [table]: [] }));
    setPreviewData(null);
  };

  const clearAllSelections = () => {
    const clearedSelections = {};
    Object.keys(selections).forEach(table => {
      clearedSelections[table] = [];
    });
    setSelections(clearedSelections);
    setExcludedYearFields(new Set());
    setPreviewData(null);
  };

  // Remove a specific column/field from selections
  const removeColumn = (table, field, year = null) => {
    if (year && YEAR_BASED_TABLES.includes(table)) {
      // For year-based fields, exclude only this specific year×field combo,
      // keeping the same field intact for other selected years.
      setExcludedYearFields(prev => {
        const next = new Set(prev);
        next.add(`${year}_${table}_${field}`);
        return next;
      });
    } else {
      setSelections(prev => ({
        ...prev,
        [table]: (prev[table] || []).filter(f => f !== field)
      }));
    }
    setPreviewData(null);
  };

  // Remove a specific year from year-based exports
  const removeYear = (yearToRemove) => {
    setSelectedYears(prev => prev.filter(y => y !== yearToRemove));
    // Clear any per-year exclusions tied to this year (they become moot).
    setExcludedYearFields(prev => {
      const next = new Set(prev);
      let changed = false;
      next.forEach(combo => {
        if (combo.startsWith(`${yearToRemove}_`)) {
          next.delete(combo);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setPreviewData(null);
  };

  const handlePreview = async (overrides = null) => {
    // `overrides` lets a caller (e.g. handleLoadSearch) pass selections /
    // years / excluded fields directly when state was just batched in the
    // same tick — bypasses React's stale-closure trap so the operator gets
    // the preview for the search they just clicked.
    const effSelections = overrides?.selections ?? selections;
    const effYears = overrides?.years ?? selectedYears;
    const effExcluded = overrides?.excludedYearFields ?? Array.from(excludedYearFields);

    const fieldsCount = Object.values(effSelections).reduce(
      (n, fs) => n + (Array.isArray(fs) ? fs.length : 0),
      0
    );
    if (fieldsCount === 0) {
      toast.error('Please select at least one field');
      return;
    }

    setPreviewLoading(true);
    toast.info('Loading all records... This may take a moment.');
    try {
      const response = await authJsonFetch(`${API}/export-data/merged-preview`, {
        method: 'POST',
        body: JSON.stringify({
          selections: effSelections,
          years: effYears,
          excluded_year_fields: effExcluded,
          limit: 100000  // Fetch all records
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setPreviewDialogOpen(true);

        // Fetch job flags for the selected years in parallel. Non-fatal —
        // if this fails, the Flags column just shows empty chip strips
        // and the operator can still click to add flags.
        (async () => {
          try {
            const yearsCsv = effYears.join(',');
            const flagsRes = await authFetch(`${API}/job-flags/by-year?years=${encodeURIComponent(yearsCsv)}`);
            if (!flagsRes.ok) return;
            const flagsJson = await flagsRes.json();
            const flagsByYear = flagsJson.flags || {};
            // Flatten into { orgId: [{...flag, year: <int>}] }
            const byOrg = {};
            Object.entries(flagsByYear).forEach(([yearStr, orgMap]) => {
              const yr = parseInt(yearStr, 10);
              Object.entries(orgMap || {}).forEach(([orgId, flagList]) => {
                (byOrg[orgId] ||= []);
                (flagList || []).forEach(f => byOrg[orgId].push({ ...f, year: yr }));
              });
            });
            setPreviewFlagsByOrg(byOrg);
          } catch (e) {
            // Silent — chips just show empty for now.
          }
        })();

        // If a saved search was just loaded with view state, restore it now
        // instead of resetting. The ref is cleared after consumption so a
        // subsequent fresh preview won't accidentally reapply old state.
        const pending = pendingViewStateRef.current;
        if (pending) {
          setPreviewFilters(pending.filters || {});
          setPreviewSortColumn(pending.sortColumn || null);
          setPreviewSortDirection(pending.sortDirection || 'asc');
          setPreviewColumnWidths(pending.columnWidths || {});
          setPreviewColumnOrder(pending.columnOrder || null);
          pendingViewStateRef.current = null;
          toast.success(
            `Loaded ${data.total_count || 0} records with saved view applied`
          );
        } else {
          // Reset sorting and filtering state for a clean preview run.
          setPreviewSortColumn(null);
          setPreviewSortDirection('asc');
          setPreviewFilters({});
          setPreviewColumnOrder(null);
          toast.success(`Loaded ${data.total_count || 0} records`);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Preview failed');
      }
    } catch (err) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    if (totalSelectedFields === 0) {
      toast.error('Please select at least one field');
      return;
    }

    setDownloading(true);
    toast.info('Preparing export... This may take a moment for large datasets.');
    
    try {
      // Get auth token
      const token = localStorage.getItem('auth_token');
      
      // Use fetch with streaming for large file support
      // Include credentials for cookie-based auth
      const response = await fetch(`${API}/export-data/merged-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',  // Important for cookie-based auth
        body: JSON.stringify({
          selections,
          years: selectedYears,
          excluded_year_fields: Array.from(excludedYearFields),
          limit: 100000  // Support larger exports
        })
      });

      if (response.ok) {
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `export_organisations_${selectedYears.join('_')}.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1].replace(/['"]/g, '');
        }
        
        // Get content length if available
        const contentLength = response.headers.get('Content-Length');
        const totalSize = contentLength ? parseInt(contentLength) : null;
        
        // Stream the response
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          chunks.push(value);
          receivedLength += value.length;
          
          // Log progress for large files
          if (totalSize && receivedLength % (5 * 1024 * 1024) < 65536) {
            const progress = ((receivedLength / totalSize) * 100).toFixed(1);
            console.log(`Download progress: ${progress}%`);
          }
        }
        
        // Combine chunks and create blob
        const blob = new Blob(chunks, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        const sizeMB = (receivedLength / (1024 * 1024)).toFixed(2);
        toast.success(`Download complete (${sizeMB} MB)`);
      } else {
        let errorMsg = 'Download failed';
        try {
          const err = await response.json();
          errorMsg = err.detail || errorMsg;
        } catch {
          errorMsg = `Download failed (${response.status})`;
        }
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download: ' + (err.message || 'Network error'));
    } finally {
      setDownloading(false);
    }
  };

  // Filter schema based on search - now filters fields within tables too
  const filteredSchema = useMemo(() => {
    if (!searchQuery) return schema;
    
    const query = searchQuery.toLowerCase();
    const filtered = {};
    
    Object.entries(schema).forEach(([tableName, tableData]) => {
      // Check if table name/label matches
      const tableMatches = tableName.toLowerCase().includes(query) || tableData.label?.toLowerCase().includes(query);
      
      // Filter fields that match the search
      const matchingFields = {};
      Object.entries(tableData.fields || {}).forEach(([fieldName, fieldData]) => {
        if (fieldName.toLowerCase().includes(query) || fieldData.label?.toLowerCase().includes(query)) {
          matchingFields[fieldName] = fieldData;
        }
      });
      
      // Include table if table matches OR has matching fields
      if (tableMatches) {
        // If table itself matches, show all fields
        filtered[tableName] = tableData;
      } else if (Object.keys(matchingFields).length > 0) {
        // If only fields match, show only those fields
        filtered[tableName] = {
          ...tableData,
          fields: matchingFields
        };
      }
    });
    
    return filtered;
  }, [schema, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className={cn("w-6 h-6", isBarbiePink && "text-pink-500")} />
            Data Management
          </h1>
          <p className="text-muted-foreground">Export and import your organisation data</p>
        </div>
      </div>
      
      {/* Tabs for Export/Import */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="export" className="flex items-center gap-2" data-testid="export-tab">
            <Download className="w-4 h-4" />
            Export Data
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="import" className="flex items-center gap-2" data-testid="import-tab">
              <Upload className="w-4 h-4" />
              Import Data
            </TabsTrigger>
          )}
        </TabsList>
        
        {/* Export Tab Content */}
        <TabsContent value="export" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Table/Field Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Select Data
                </CardTitle>
                <CardDescription>Choose tables and fields to export</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Year Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Select Years (for year-based data)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {dynamicYearOptions.map(year => (
                      <Button
                        key={year}
                        variant={selectedYears.includes(year) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleYear(year)}
                        className={cn(
                          "transition-colors",
                          selectedYears.includes(year) && isBarbiePink && "bg-pink-500 hover:bg-pink-600"
                        )}
                        data-testid={`year-${year}`}
                      >
                        {year}
                      </Button>
                    ))}
                    {managedYears.isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const input = window.prompt('Add a new year:', String(new Date().getFullYear() + 1));
                          if (!input) return;
                          managedYears.addYear(parseInt(input, 10));
                        }}
                        className="text-xs text-primary"
                        data-testid="dataview-year-add"
                      >
                        + Add year
                      </Button>
                    )}
                  </div>
                </div>

                {/* Saved Searches - Collapsible */}
                <Collapsible open={savedSearchesOpen} onOpenChange={setSavedSearchesOpen}>
                  <div className="p-3 border rounded-lg bg-muted/30">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                          {savedSearchesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <FileSpreadsheet className="w-4 h-4" />
                          Saved Searches
                          {savedSearches.length > 0 && (
                            <Badge variant="secondary" className="text-[10px]">{savedSearches.length}</Badge>
                          )}
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Pre-tick "save view state" if there's anything
                            // worth saving (active filters, an active sort,
                            // or custom column widths) — saves the operator
                            // a click in the common case.
                            const hasActiveFilters = Object.values(previewFilters).some(isFilterActive);
                            const hasSort = !!previewSortColumn;
                            const hasWidths = Object.keys(previewColumnWidths).length > 0;
                            const hasOrder = !!(previewColumnOrder && previewColumnOrder.length);
                            setSaveForm(prev => ({
                              ...prev,
                              save_view_state: hasActiveFilters || hasSort || hasWidths || hasOrder
                            }));
                            setSaveDialogOpen(true);
                          }}
                          disabled={totalSelectedFields === 0}
                          data-testid="save-search-btn"
                        >
                          <Download className="w-3 h-3 mr-1 rotate-180" />
                          Save Current
                        </Button>
                      </div>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent className="mt-2">
                      {loadingSavedSearches ? (
                        <div className="text-sm text-muted-foreground text-center py-2">
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                          Loading...
                        </div>
                      ) : savedSearches.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          No saved searches yet. Configure your export and click "Save Current"
                        </p>
                      ) : (
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {savedSearches.map(search => (
                            <div
                              key={search.search_id}
                              className="flex items-center justify-between p-2 rounded hover:bg-muted/50 group"
                            >
                              <button
                                onClick={() => handleLoadSearch(search)}
                                className="flex-1 text-left text-sm truncate"
                                data-testid={`load-search-${search.search_id}`}
                              >
                                <span className="font-medium">{search.name}</span>
                                {search.has_view_state && (
                                  <Badge
                                    className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                                    variant="outline"
                                    title="Includes saved filters, sort, and column widths"
                                    data-testid={`view-badge-${search.search_id}`}
                                  >
                                    View
                                  </Badge>
                                )}
                                {search.is_shared && (
                                  <Badge variant="outline" className="ml-2 text-[10px]">Shared</Badge>
                                )}
                                {search.description && (
                                  <span className="text-muted-foreground ml-2 text-xs">
                                    {search.description.slice(0, 30)}
                                  </span>
                                )}
                              </button>
                              {canEditSearch(search) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSearch(search);
                                  }}
                                  className="h-6 w-6 p-0 hover:bg-muted"
                                  title={`Edit "${search.name}"`}
                                  data-testid={`edit-search-${search.search_id}`}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                              )}
                              {canEditSearch(search) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSearch(search.search_id, search.name);
                                  }}
                                  disabled={deletingSearch === search.search_id}
                                  className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  title={`Delete "${search.name}"`}
                                  data-testid={`delete-search-${search.search_id}`}
                                >
                                  {deletingSearch === search.search_id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3 h-3" />
                                  )}
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tables and fields..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="export-search"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Tables List */}
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-2">
                    {Object.entries(filteredSchema).map(([tableName, tableData]) => {
                      const isExpanded = expandedTables[tableName];
                      const selectedCount = selections[tableName]?.length || 0;
                      const totalFields = Object.keys(tableData.fields || {}).length;
                      const hasSelections = selectedCount > 0;
                      
                      return (
                        <div key={tableName} className="space-y-1">
                          {/* Table Header */}
                          <button
                            onClick={() => toggleTable(tableName)}
                            className={cn(
                              "w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors",
                              hasSelections
                                ? isBarbiePink
                                  ? "bg-pink-100 border border-pink-300 dark:bg-pink-950 dark:border-pink-800"
                                  : "bg-primary/10 border border-primary/30"
                                : "hover:bg-muted border border-transparent"
                            )}
                            data-testid={`table-${tableName}`}
                          >
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                              <div>
                                <span className="font-medium">{tableData.label || tableName}</span>
                                {tableData.yearBased && (
                                  <Badge variant="outline" className="ml-2 text-[10px]">per year</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{totalFields} fields</span>
                              {hasSelections && (
                                <Badge variant="default" className="text-xs">
                                  {selectedCount}
                                </Badge>
                              )}
                            </div>
                          </button>
                          
                          {/* Expanded Fields */}
                          {isExpanded && (
                            <div className="ml-6 mt-2 space-y-1 pb-2 animate-in slide-in-from-top-2 duration-200">
                              <div className="flex gap-2 mb-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => selectAllInTable(tableName)}
                                  className="text-xs h-7"
                                >
                                  <Check className="w-3 h-3 mr-1" />
                                  All
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => clearTable(tableName)}
                                  className="text-xs h-7"
                                  disabled={!hasSelections}
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Clear
                                </Button>
                              </div>
                              {Object.entries(tableData.fields || {}).map(([fieldName, fieldData]) => (
                                <label
                                  key={fieldName}
                                  className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted transition-colors"
                                >
                                  <Checkbox
                                    checked={selections[tableName]?.includes(fieldName)}
                                    onCheckedChange={() => toggleField(tableName, fieldName)}
                                    data-testid={`field-${tableName}-${fieldName}`}
                                  />
                                  <span className="text-sm flex-1">{fieldData.label || fieldName}</span>
                                  <Badge variant="secondary" className={cn("text-[10px]", getTypeBadgeColor(fieldData.type))}>
                                    {fieldData.type}
                                  </Badge>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Right Column - Preview & Download */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Preview & Export
                    </CardTitle>
                    <CardDescription>
                      {totalSelectedFields} fields selected across {selectedYears.length} year(s)
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      disabled={previewLoading || totalSelectedFields === 0}
                      data-testid="preview-btn"
                    >
                      {previewLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Preview
                    </Button>
                    <Button
                      onClick={handleDownload}
                      disabled={downloading || totalSelectedFields === 0}
                      className={cn(isBarbiePink && "bg-pink-500 hover:bg-pink-600")}
                      data-testid="download-btn"
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Column Preview */}
                {columnHeaders.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Columns ({columnHeaders.length})</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAllSelections}
                        className="h-7 text-xs text-destructive"
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1 p-3 bg-muted/50 rounded-lg max-h-48 overflow-y-auto">
                      {columnHeaders.map((col, idx) => (
                        <Badge 
                          key={idx}
                          variant="secondary"
                          className="text-xs pr-1 flex items-center gap-1 group"
                        >
                          <span>{col.label}</span>
                          <button
                            onClick={() => removeColumn(col.table, col.field, col.year)}
                            className="ml-1 rounded-full hover:bg-destructive/20 p-0.5 opacity-50 group-hover:opacity-100"
                            title={`Remove ${col.label}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    {/* Selected Years */}
                    {selectedYears.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-muted-foreground">Years included: </span>
                        <div className="inline-flex flex-wrap gap-1 ml-1">
                          {selectedYears.sort((a, b) => b - a).map(year => (
                            <Badge 
                              key={year}
                              variant="outline"
                              className="text-xs pr-1 flex items-center gap-1"
                            >
                              <span>{year}</span>
                              <button
                                onClick={() => removeYear(year)}
                                className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                                title={`Remove year ${year}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Preview Status */}
                <div className="flex flex-col items-center justify-center h-[300px] text-center text-muted-foreground">
                  <Database className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-lg font-medium">
                    {previewData ? `${previewData.total_count || 0} records available` : 'No preview yet'}
                  </p>
                  <p className="text-sm">
                    {totalSelectedFields === 0
                      ? 'Select fields from tables on the left'
                      : previewData 
                        ? 'Click Preview to view data in a popup'
                        : 'Click Preview to see your data'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Import Tab Content (Admin Only) */}
        {isAdmin && (
          <TabsContent value="import" className="mt-6">
            <ImportSection isBarbiePink={isBarbiePink} />
          </TabsContent>
        )}
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={(open) => {
        setPreviewDialogOpen(open);
        if (!open) setQuickViewOrgId(null);
      }}>
        <DialogContent className="max-w-[98vw] w-[98vw] max-h-[96vh] h-[96vh] flex flex-col p-4">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Data Preview
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between">
              <span>
                Showing {filteredAndSortedPreview.length} of {previewData?.total_count || 0} records
                {hasAnyActiveFilter && ' (filtered)'}
                . Click an org name to view full details below.
              </span>
              {(hasAnyActiveFilter || previewSortColumn) && (
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs">
                  <X className="w-3 h-3 mr-1" />
                  Clear All Filters
                </Button>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div
              className={cn(
                "flex-1 min-h-0 grid gap-3",
                quickViewExpanded
                  ? "grid-rows-[minmax(0,1fr)_minmax(0,2fr)]"
                  : "grid-rows-[minmax(0,1fr)_auto]"
              )}
            >
              {/* Top — preview table */}
              <div className="overflow-auto border rounded-lg relative preview-scroll">
              <table className="caption-bottom text-sm border-collapse" style={{ width: 'max-content', minWidth: '100%' }}>
                <thead className="sticky top-0 bg-background z-20 shadow-[0_1px_0_0_hsl(var(--border))]">
                  <tr className="border-b">
                    {orderedColumns.map((col, idx) => {
                      const colWidth = previewColumnWidths[col];
                      const nameCol = isNameColumn(col);
                      // Sticky-left Name column. We layer on a higher z-index
                      // so the frozen column wins over both the body cells
                      // (z-10) AND the regular thead cells (z-20). Solid
                      // background so the underlying scrolled cells don't
                      // bleed through.
                      const stickyCellStyle = nameCol
                        ? { left: 0, position: 'sticky', zIndex: 30, background: 'hsl(var(--background))' }
                        : undefined;
                      return (
                      <th
                        key={idx}
                        className={cn(
                          "whitespace-nowrap text-xs font-semibold p-0 text-left relative",
                          nameCol && "border-r shadow-[2px_0_0_0_hsl(var(--border))]"
                        )}
                        style={{
                          ...(colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px`, maxWidth: `${colWidth}px` } : {}),
                          ...(stickyCellStyle || {}),
                        }}
                        draggable
                        onDragStart={(e) => handleColumnDragStart(e, col)}
                        onDragOver={handleColumnDragOver}
                        onDrop={(e) => handleColumnDrop(e, col)}
                        data-testid={`preview-col-header-${idx}`}
                      >
                          <div className="flex flex-col">
                            {/* Column header with sort */}
                            <div className="flex items-center">
                              <button
                                onClick={() => handleSortClick(col)}
                                className="flex items-center gap-1 px-2 py-2 hover:bg-muted/50 flex-1 text-left min-w-0"
                                data-testid={`preview-sort-${idx}`}
                              >
                                <span className="truncate max-w-[130px]" title={col}>{col}</span>
                                <span className="flex-shrink-0">
                                  {previewSortColumn === col ? (
                                    previewSortDirection === 'asc' ? (
                                      <ArrowUp className="w-3 h-3 text-primary" />
                                    ) : (
                                      <ArrowDown className="w-3 h-3 text-primary" />
                                    )
                                  ) : (
                                    <ArrowUpDown className="w-3 h-3 opacity-30" />
                                  )}
                                </span>
                              </button>
                              
                              {/* Filter Popover trigger */}
                              <Popover 
                                open={openFilterPopover === col} 
                                onOpenChange={(open) => setOpenFilterPopover(open ? col : null)}
                              >
                                <PopoverTrigger asChild>
                                  <button
                                    className={cn(
                                      "flex-shrink-0 p-1.5 rounded hover:bg-muted/70 mr-1 transition-colors",
                                      isFilterActive(previewFilters[col]) && "text-primary bg-primary/10"
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                    title={`Filter ${col}`}
                                    data-testid={`preview-filter-btn-${idx}`}
                                  >
                                    <Filter className="w-3 h-3" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent 
                                  className="w-64 p-3" 
                                  align="start" 
                                  side="bottom"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-semibold truncate max-w-[160px]" title={col}>
                                        Filter: {col}
                                      </span>
                                      {isFilterActive(previewFilters[col]) && (
                                        <button
                                          onClick={() => clearColumnFilter(col)}
                                          className="text-xs text-muted-foreground hover:text-foreground"
                                          data-testid={`preview-filter-clear-${idx}`}
                                        >
                                          Clear
                                        </button>
                                      )}
                                    </div>
                                    
                                    {/* Text search */}
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Search text</label>
                                      <Input
                                        type="text"
                                        placeholder="Contains..."
                                        value={getColumnFilter(col).text}
                                        onChange={(e) => handleFilterChange(col, 'text', e.target.value)}
                                        className="h-7 text-xs"
                                        data-testid={`preview-filter-text-${idx}`}
                                      />
                                    </div>
                                    
                                    {/* Show Blanks / Hide Blanks */}
                                    <div className="space-y-1.5">
                                      <div className="flex items-center gap-2">
                                        <Checkbox
                                          id={`blanks-${col}`}
                                          checked={getColumnFilter(col).showBlanks}
                                          onCheckedChange={(checked) => handleFilterChange(col, 'showBlanks', !!checked)}
                                          data-testid={`preview-filter-blanks-${idx}`}
                                        />
                                        <label htmlFor={`blanks-${col}`} className="text-xs cursor-pointer select-none">
                                          Show Blanks Only
                                        </label>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Checkbox
                                          id={`hide-blanks-${col}`}
                                          checked={getColumnFilter(col).hideBlanks}
                                          onCheckedChange={(checked) => handleFilterChange(col, 'hideBlanks', !!checked)}
                                          data-testid={`preview-filter-hide-blanks-${idx}`}
                                        />
                                        <label htmlFor={`hide-blanks-${col}`} className="text-xs cursor-pointer select-none">
                                          Hide Blanks
                                        </label>
                                      </div>
                                    </div>
                                    
                                    {/* Month filter - only for date fields */}
                                    {isDateField(col) && (
                                      <div>
                                        <label className="text-xs text-muted-foreground mb-1.5 block">Filter by Month</label>
                                        <div className="grid grid-cols-4 gap-1">
                                          {MONTHS.map((month, mIdx) => {
                                            const colFilter = getColumnFilter(col);
                                            const isSelected = colFilter.months.includes(mIdx);
                                            return (
                                              <button
                                                key={mIdx}
                                                onClick={() => toggleMonthFilter(col, mIdx)}
                                                className={cn(
                                                  "text-xs px-1.5 py-1 rounded border transition-colors",
                                                  isSelected 
                                                    ? "bg-primary text-primary-foreground border-primary" 
                                                    : "hover:bg-muted border-border"
                                                )}
                                                data-testid={`preview-filter-month-${idx}-${mIdx}`}
                                              >
                                                {month}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                          {/* Column resize handle */}
                          <div
                            onMouseDown={(e) => startColumnResize(e, col)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none z-30 hover:bg-primary/40 active:bg-primary/60"
                            title="Drag to resize column"
                            data-testid={`preview-col-resize-${idx}`}
                          />
                        </th>
                      );
                    })}
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {filteredAndSortedPreview.map((row, rowIdx) => {
                      const orgIdRow = row['org_id'] || row['_org_id'];
                      const isSelectedRow = orgIdRow && orgIdRow === quickViewOrgId;
                      // Zebra striping — every 2nd row gets a slightly
                      // darker tint so the eye can track wide tables
                      // without a finger on the screen. We use `muted/40`
                      // which is subtle enough to still let the
                      // primary-row selection state win when hovered.
                      const isEven = rowIdx % 2 === 1;
                      return (
                      <tr
                        key={rowIdx}
                        onClick={() => {
                          if (orgIdRow) {
                            requestSwitchOrg(orgIdRow);
                          }
                        }}
                        className={cn(
                          "border-b transition-colors cursor-pointer",
                          isSelectedRow
                            ? "bg-primary/10 hover:bg-primary/15"
                            : isEven
                              ? "bg-muted/40 hover:bg-muted/60"
                              : "hover:bg-muted/50"
                        )}
                        data-testid={`preview-row-${rowIdx}`}
                      >
                        {previewData.columns?.length && orderedColumns.map((col, colIdx) => {
                          const value = row[col];
                          const nameCol = isNameColumn(col);
                          const orgId = orgIdRow;
                          const colWidth = previewColumnWidths[col];
                          // The sticky Name column needs an OPAQUE background
                          // so the scrolled cells underneath don't show
                          // through. The row tints (selected/zebra) are
                          // intentionally translucent for visual lightness;
                          // for the sticky cell we composite those tints
                          // ON TOP of a solid --background base via a
                          // layered linear-gradient, which keeps the same
                          // colour vibe while being fully opaque.
                          const stickyBg = isSelectedRow
                            ? 'linear-gradient(hsl(var(--primary) / 0.10), hsl(var(--primary) / 0.10)), hsl(var(--background))'
                            : isEven
                              ? 'linear-gradient(hsl(var(--muted) / 0.40), hsl(var(--muted) / 0.40)), hsl(var(--background))'
                              : 'hsl(var(--background))';
                          const cellStyle = {
                            ...(colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px`, maxWidth: `${colWidth}px` } : {}),
                            ...(nameCol ? { left: 0, position: 'sticky', zIndex: 10, background: stickyBg } : {}),
                          };
                          
                          // Format the value (including date formatting)
                          const displayValue = formatCellValue(value, col);

                          // Virtual Flags column — render colored chips backed by
                          // `previewFlagsByOrg`. Filter/sort still work because
                          // we stamp a text signature into row[FLAGS_COL] above.
                          if (col === FLAGS_COL) {
                            return (
                              <td
                                key={colIdx}
                                className="text-xs py-2 px-2"
                                style={cellStyle}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`preview-flags-cell-${rowIdx}`}
                              >
                                {orgId ? (
                                  <JobFlagsCell
                                    orgId={orgId}
                                    years={selectedYears}
                                    flags={previewFlagsByOrg[orgId] || []}
                                    onChange={(next) => {
                                      setPreviewFlagsByOrg(prev => ({ ...prev, [orgId]: next }));
                                    }}
                                  />
                                ) : (
                                  <span className="text-muted-foreground/60">-</span>
                                )}
                              </td>
                            );
                          }

                          // Make organization name clickable — selects the row for quick-view (no navigation)
                          if (nameCol && orgId && value) {
                            return (
                              <td 
                                key={colIdx} 
                                className={cn(
                                  "text-xs py-2 px-2 truncate",
                                  nameCol && "border-r shadow-[2px_0_0_0_hsl(var(--border))]"
                                )}
                                style={cellStyle}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    requestSwitchOrg(orgId);
                                  }}
                                  className={cn(
                                    "hover:underline flex items-center gap-1 truncate",
                                    isSelectedRow ? "text-primary font-semibold" : "text-blue-600 hover:text-blue-800"
                                  )}
                                  title={`View ${value} details below`}
                                  data-testid={`preview-org-link-${rowIdx}`}
                                >
                                  <span className="truncate">{String(displayValue).slice(0, 100)}</span>
                                </button>
                              </td>
                            );
                          }
                          
                          return (
                            <td 
                              key={colIdx} 
                              className={cn(
                                "text-xs py-2 px-2 truncate",
                                nameCol && "border-r shadow-[2px_0_0_0_hsl(var(--border))]"
                              )}
                              title={String(displayValue ?? '')}
                              style={cellStyle}
                            >
                              {displayValue !== '-' 
                                ? String(displayValue).slice(0, 100)
                                : '-'}
                            </td>
                          );
                        })}
                      </tr>
                      );
                    })}
                    {filteredAndSortedPreview.length === 0 && (
                      <tr>
                        <td colSpan={orderedColumns.length || 1} className="text-center py-8 text-muted-foreground">
                          No records match your filter criteria
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {Object.keys(columnTotals).length > 0 && (
                    <tfoot className="sticky bottom-0 bg-muted/95 border-t-2 z-20 backdrop-blur-sm">
                      <tr data-testid="preview-totals-row">
                        {orderedColumns.map((col, idx) => {
                          const colWidth = previewColumnWidths[col];
                          const nameCol = isNameColumn(col);
                          const cellStyle = {
                            ...(colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px`, maxWidth: `${colWidth}px` } : {}),
                            ...(nameCol ? { left: 0, position: 'sticky', zIndex: 25, background: 'hsl(var(--muted))' } : {}),
                          };
                          const total = columnTotals[col];
                          return (
                            <td
                              key={idx}
                              className={cn(
                                "text-xs py-2 px-2 font-semibold truncate",
                                nameCol && "border-r shadow-[2px_0_0_0_hsl(var(--border))]"
                              )}
                              style={cellStyle}
                              data-testid={total !== undefined ? `preview-total-${col}` : undefined}
                              title={total !== undefined ? `Sum of ${col}` : undefined}
                            >
                              {total !== undefined ? formatTotal(total) : ''}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Bottom — Organisation Quick View (collapsible, default collapsed) */}
              <div className="border rounded-lg overflow-hidden bg-card flex flex-col">
                <button
                  type="button"
                  onClick={() => setQuickViewExpanded((v) => !v)}
                  className="flex items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted/50 transition-colors flex-shrink-0 border-b"
                  data-testid="quickview-toggle"
                >
                  <span className="flex items-center gap-2">
                    {quickViewExpanded ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                    Organisation Details
                    {quickViewOrgId ? (
                      <span className="text-muted-foreground font-normal">
                        — {quickViewExpanded ? 'click rows above to switch' : 'click to expand'}
                      </span>
                    ) : (
                      <span className="text-muted-foreground font-normal">
                        — select an organisation row above
                      </span>
                    )}
                  </span>
                </button>
                {quickViewExpanded && (
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <OrgQuickView
                      orgId={quickViewOrgId}
                      year={quickViewYear}
                      onYearChange={setQuickViewYear}
                      activeTab={quickViewTab}
                      onTabChange={setQuickViewTab}
                      isBarbiePink={isBarbiePink}
                      onDirtyChange={setQuickViewDirty}
                      saveHandlerRef={quickViewSaveRef}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <UnsavedChangesDialog
            open={switchOrgDialog.open}
            onOpenChange={(v) => { if (!v) setSwitchOrgDialog({ open: false, targetOrgId: null }); }}
            title="Unsaved changes in Organisation Details"
            description="You have unsaved edits in the panel below. Save them, discard them, or stay on the current organisation?"
            onSave={async () => {
              if (!quickViewSaveRef.current) return;
              const ok = await quickViewSaveRef.current();
              // If the save failed (toast surfaced the error) we keep the
              // operator on the current org and leave the dialog closed —
              // they can retry from the panel's own Save button.
              return ok !== false;
            }}
            onDiscard={() => { setQuickViewDirty(false); }}
            onContinue={() => {
              const next = switchOrgDialog.targetOrgId;
              setSwitchOrgDialog({ open: false, targetOrgId: null });
              if (next) {
                setQuickViewOrgId(next);
                setQuickViewExpanded(true);
              }
            }}
          />
          
          <DialogFooter className="flex-shrink-0">
            {/* Bulk Email Campaign — opens an inline dialog with all of the
                Email Hub campaign settings (template, subject, body, AI
                Assist, attachments, schedule). The exact org_ids from the
                currently-filtered Data Preview are passed directly to the
                dialog so the recipient set can't be polluted by any other
                org fetch. */}
            <Button
              variant="outline"
              onClick={() => {
                const ids = [];
                const seen = new Set();
                filteredAndSortedPreview.forEach((row) => {
                  const oid = row['org_id'] || row['_org_id'];
                  if (oid && !seen.has(oid)) {
                    seen.add(oid);
                    ids.push(oid);
                  }
                });
                if (ids.length === 0) {
                  toast.error('No organisations in the filtered results to email');
                  return;
                }
                setBulkDialogOrgIds(ids);
                setBulkDialogOpen(true);
              }}
              disabled={filteredAndSortedPreview.length === 0}
              data-testid="bulk-email-from-preview-btn"
              className="text-blue-700 border-blue-300 hover:bg-blue-50"
              title="Compose and schedule a bulk email to the orgs currently in this filtered view"
            >
              <Mail className="w-4 h-4 mr-2" />
              Bulk Email ({filteredAndSortedPreview.length})
            </Button>
            {loadedView ? (
              <>
                {canEditSearch(loadedView) && (
                  <Button
                    variant="outline"
                    onClick={handleUpdateLoadedView}
                    disabled={updatingView || totalSelectedFields === 0}
                    data-testid="update-loaded-view-btn"
                    title={`Update "${loadedView.name}" with current selections and table view`}
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  >
                    {updatingView ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Update "{loadedView.name.length > 22 ? loadedView.name.slice(0, 22) + '…' : loadedView.name}"
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setSaveForm({ name: '', description: '', is_shared: false, save_view_state: true });
                    setSaveDialogOpen(true);
                  }}
                  disabled={totalSelectedFields === 0}
                  data-testid="save-as-new-view-btn"
                  title={canEditSearch(loadedView)
                    ? "Save current selections + table view as a brand-new entry"
                    : "You can't edit someone else's saved search — save your changes as a new entry instead"}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Save as New
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  setSaveForm(prev => ({
                    ...prev,
                    save_view_state: true,
                  }));
                  setSaveDialogOpen(true);
                }}
                disabled={totalSelectedFields === 0}
                data-testid="save-preview-view-btn"
                title="Save current selections + filters + sort + column widths"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Save View
              </Button>
            )}
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className={cn(isBarbiePink && "bg-pink-500 hover:bg-pink-600")}
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download Full Export
            </Button>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Search Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={(open) => {
        if (!open) handleCancelEdit();
        else setSaveDialogOpen(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              {editingSearch ? 'Update Search Configuration' : 'Save Search Configuration'}
            </DialogTitle>
            <DialogDescription>
              {editingSearch 
                ? `Update "${editingSearch.name}" with your current field selections`
                : 'Save your current field selections and years for easy reuse'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="search-name">Name *</Label>
              <Input
                id="search-name"
                placeholder="e.g., Q4 Export, Sales Summary..."
                value={saveForm.name}
                onChange={(e) => setSaveForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="save-search-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="search-description">Description (optional)</Label>
              <Input
                id="search-description"
                placeholder="Brief description..."
                value={saveForm.description}
                onChange={(e) => setSaveForm(prev => ({ ...prev, description: e.target.value }))}
                data-testid="save-search-description"
              />
            </div>
            
            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                id="is-shared"
                checked={saveForm.is_shared}
                onCheckedChange={(checked) => setSaveForm(prev => ({ ...prev, is_shared: !!checked }))}
                data-testid="save-search-shared"
              />
              <Label htmlFor="is-shared" className="text-sm font-normal cursor-pointer">
                Share with all users (visible to everyone)
              </Label>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="save-view-state"
                checked={saveForm.save_view_state}
                onCheckedChange={(checked) => setSaveForm(prev => ({ ...prev, save_view_state: !!checked }))}
                data-testid="save-search-view-state"
              />
              <Label htmlFor="save-view-state" className="text-sm font-normal cursor-pointer leading-tight">
                Save current view state (filters, sort, columns)
                <span className="block text-[11px] text-muted-foreground mt-0.5">
                  When loaded next time, the table will reopen with the same filters, sort and column widths.
                </span>
              </Label>
            </div>

            {/* Summary */}
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <p className="font-medium mb-1">Current Configuration:</p>
              <p className="text-muted-foreground">
                {totalSelectedFields} fields selected, {selectedYears.length} year(s): {selectedYears.join(', ')}
              </p>
              {saveForm.save_view_state && (
                <p className="text-muted-foreground mt-1 text-xs">
                  + {Object.values(previewFilters).filter(isFilterActive).length} column filter(s),
                  {previewSortColumn ? ` sort by ${previewSortColumn} (${previewSortDirection}),` : ' no sort,'}
                  {' '}{Object.keys(previewColumnWidths).length} custom column width(s)
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSearch}
              disabled={savingSearch || !saveForm.name.trim()}
              className={cn(isBarbiePink && "bg-pink-500 hover:bg-pink-600")}
              data-testid="confirm-save-search"
            >
              {savingSearch ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {editingSearch ? 'Update Search' : 'Save Search'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline Bulk Email Campaign — handles the whole flow in-place,
          replacing the legacy prefill-into-Email-Hub path which would
          intermittently leak the default org list into the
          selection. */}
      <BulkEmailCampaignDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        orgIds={bulkDialogOrgIds}
      />
    </div>
  );
};

export default ExportDataPage;
